// ============================================================================
// JARVISTRAVEL - SAFE URL
// Version: 1.0.0
// The single scheme allowlist for every dynamic href the app renders.
// Property/trip/explore values arrive from the backend and are never trusted:
// a javascript:/data:/vbscript: value that reaches an href executes in the
// signed-in user's session on click (stored XSS). Allowlisting the schemes we
// actually navigate to — rather than denylisting the dangerous ones — is what
// makes obfuscation (casing, control characters, embedded whitespace) moot.
// ============================================================================

// Schemes the app navigates to. `/` and `#` cover in-app paths and anchors.
// `geo:` is the Android map hand-off (JAR-2701): a non-executing navigation
// scheme the maps feature emits so the phone opens its own maps app. It cannot
// run script or navigate to a web origin, so it widens nothing dangerous.
//
// The leading-slash alternation deliberately refuses a SECOND slash or a
// backslash. `//evil.com` and `/\evil.com` are protocol-relative: a browser
// resolves both against the current scheme and navigates off-site, so a bare
// `\/` alternation would have matched their first character and passed an
// attacker origin straight into an href. Not script execution — no dangerous
// scheme survives — but off-site navigation from backend-supplied data, which
// is the same class this module exists to stop. In-app paths (`/trips/1`, `/`)
// are unaffected.
const SAFE_HREF = /^(https?:|geo:|tel:|mailto:|#|\/(?![/\\]))/i;

/** True when `url` uses an allowlisted scheme and is safe to render as an
 *  href. Empty/nullish input is not safe — there is nothing to link to. */
export const isSafeHref = (url?: string | null): boolean => SAFE_HREF.test((url || '').trim());

/** Trimmed href when the scheme is allowlisted, otherwise `null` — for call
 *  sites that should hide the affordance rather than render a dead link. */
export const safeHrefOrNull = (url?: string | null): string | null => {
  const trimmed = (url || '').trim();
  return SAFE_HREF.test(trimmed) ? trimmed : null;
};

/** Trimmed href when the scheme is allowlisted, otherwise `'#'` — for call
 *  sites that must always render an anchor because layout depends on it. */
export const safeHref = (url?: string | null): string => safeHrefOrNull(url) ?? '#';

// http(s) only. Values that are semantically an *external site* — a property's
// website, a provider link — should not be able to become a tel:, mailto:, or
// protocol-relative `//host` link just because those are safe elsewhere. The
// looser SAFE_HREF above exists for LinkLabel, which legitimately renders call
// and email affordances; using it for an external URL would be wider than the
// data ever needs. Parsed rather than pattern-matched so `//evil.com` and
// backslash variants cannot slip through.
const isHttpScheme = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/** Trimmed href when the value is an absolute http(s) URL, otherwise `null`.
 *  Use for backend-supplied external site links. */
export const safeHttpUrlOrNull = (url?: string | null): string | null => {
  const trimmed = (url || '').trim();
  return isHttpScheme(trimmed) ? trimmed : null;
};
